import classNames from 'classnames'
import moment from 'moment'
import { Button } from 'app/components/ui/button'
import { DAY_KEY, visibleRange } from 'lib/taskCalendar'
import type { TaskJson } from 'app/types/entities'

/**
 * The phone calendar's week strip (Google Calendar and the Apple Calendar
 * app): one row of dates, the selected one filled, today in brand blue, and a
 * dot under days that have tasks. The Day view's phone header and the Action
 * Center's My tasks card both use it.
 */
export function TaskWeekStrip({
  anchor,
  tasksByDay,
  onOpenDay,
}: {
  anchor: moment.Moment
  tasksByDay: Map<string, TaskJson[]>
  onOpenDay: (day: moment.Moment) => void
}) {
  const { start } = visibleRange(`week`, anchor)
  const today = moment().format(DAY_KEY)
  return (
    <div className="grid grid-cols-7 gap-1">
      {Array.from({ length: 7 }, (_, i) => start.clone().add(i, `days`)).map((day) => {
        const key = day.format(DAY_KEY)
        const isSelected = day.isSame(anchor, `day`)
        return (
          <Button
            key={key}
            variant="ghost"
            size="row"
            aria-pressed={isSelected}
            aria-label={day.format(`dddd, MMMM D`)}
            onClick={() => onOpenDay(day)}
          >
            <span className="flex w-full flex-col items-center gap-0.5">
              <span className="text-caption-sm uppercase text-subtle">{day.format(`dd`)[0]}</span>
              <span
                className={classNames(
                  `flex h-8 w-8 items-center justify-center rounded-full text-ui-sm`,
                  // Today stays brand blue even when selected, as in Google Calendar;
                  // the inverse fill marks a different selected day.
                  key === today
                    ? `bg-primary-600 text-on-color`
                    : isSelected
                      ? `bg-inverse text-inverse`
                      : `text-strong`,
                )}
              >
                {day.date()}
              </span>
              <span
                className={classNames(
                  `h-1 w-1 rounded-full`,
                  tasksByDay.get(key)?.length ? `bg-(--fg-minimal)` : `bg-transparent`,
                )}
              />
            </span>
          </Button>
        )
      })}
    </div>
  )
}
